import { z } from 'zod';
import type { ApprovalAmount } from '../approvals.js';
import { DomainError } from '../errors.js';
import { MATERIAL_UNITS, toIssues } from '../materials/catalog.js';
import { portCodeField } from '../ports/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Malzeme talebi (ADR 0026 §1, §3; `satin-alma-modulu.md` §1 "Talep bilgileri";
 * `satin-alma-akisi.jpeg` "New Material Requisition"). Talep gemide açılır, gemi verisidir; onayı
 * tutar kademesine göre gemide ve ofiste adımlarla yürür. Hata iletileri koddur; arayüz çevirir.
 */

/** Onay matrisindeki kayıt türü (ADR 0026 "Sonuçlar"). */
export const REQUISITION_RECORD = 'requisition';

/**
 * Talebi açan bölüm. Tasarımdaki bölümler (Engine, Deck, Galley, Cabin, Safety) ve müşterinin
 * kategorilerinden gemide bölüm olanlar (`satin-alma-modulu.md` §18); kumanya ve "Other" ortağın
 * notu #7 (ADR 0047 §4). Kolon serbest metindir; liste yalnız şemada.
 */
export const REQUISITION_DEPARTMENTS = [
  'Deck',
  'Engine',
  'Electrical',
  'Navigation',
  'Galley',
  'Cabin',
  'Safety',
  'Medical',
  'Provisions',
  'Other',
] as const;
export type RequisitionDepartment = (typeof REQUISITION_DEPARTMENTS)[number];

/** Satır önceliği: tasarımda "Normal" ve "High"; müşterinin "Urgent Purchases" listesi. */
export const REQUISITION_PRIORITIES = ['Normal', 'High', 'Urgent'] as const;
export type RequisitionPriority = (typeof REQUISITION_PRIORITIES)[number];

/** Talep nedeni / bağlantısı (`satin-alma-modulu.md` §1). */
export const PURCHASE_REASONS = [
  'Maintenance',
  'Safety',
  'Navigation',
  'Deck',
  'Engine',
  'Hotel',
  'Galley',
  'Crew',
  'Provision',
  'Spare Parts',
  'Survey',
  'Audit',
  'Deficiency',
  'Planned Maintenance',
  'Emergency',
] as const;
export type PurchaseReason = (typeof PURCHASE_REASONS)[number];

/**
 * Şirketin temel para birimi ayarlanmamışsa (sorular #76): tasarımdaki tutarlar USD'dir. Onay
 * kademeleri bu para biriminde karşılaştırılır (ADR 0026 §3).
 */
export const DEFAULT_BASE_CURRENCY = 'USD';

/** En çok satır: bir talep bir sipariş tutarında kalır, liste ekranı sayfasız açılır. */
export const MAX_REQUISITION_LINES = 200;
const MAX_QTY = 1_000_000_000;
const MAX_PRICE = 1_000_000_000;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));
const localDate = () => z.iso.date(typed('INVALID_DATE'));

/** Talep satırı: katalogdan seçilen ya da serbest metinle yazılan kalem (sorular #73). */
export const requisitionLineSchema = z.object({
  /** Katalogdaki kalem; katalogda olmayan kalemde boş. */
  materialId: z
    .uuid(typed('INVALID_ID'))
    .nullish()
    .transform((value) => value ?? null),
  /** Kalemin adı: katalogdan kopyalanır; katalog sonradan değişse de talep içeriği değişmez. */
  name: text(200),
  description: optionalText(300),
  quantity: z.number(typed('INVALID_NUMBER')).gt(0, 'OUT_OF_RANGE').max(MAX_QTY, 'OUT_OF_RANGE'),
  unit: option(MATERIAL_UNITS),
  /** Tahmini birim fiyat (Estimated Price), talebin para biriminde; bilinmiyorsa boş. */
  unitPrice: z
    .number(typed('INVALID_NUMBER'))
    .min(0, 'OUT_OF_RANGE')
    .max(MAX_PRICE, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null),
  priority: option(REQUISITION_PRIORITIES).default('Normal'),
});
export type RequisitionLine = z.output<typeof requisitionLineSchema>;

/**
 * Talep başlığı ve satırları. Gemi, talep numarası ve talep günü kayıt katmanında gemi
 * sunucusundan yazılır. Taslak satırsız kaydedilebilir; gönderimde denetlenir.
 */
export const requisitionSchema = z.object({
  department: option(REQUISITION_DEPARTMENTS),
  /** Talep eden (Requested By): gemideki kişi. */
  requestedBy: z.uuid(typed('INVALID_ID')),
  requiredDate: localDate(),
  /** Teslim yeri (Port / Delivery Location). */
  port: optionalText(120),
  /** Listedeki liman (ADR 0048 §3); içerik hash'ine girmez, metin (`port`) girer. */
  portCode: portCodeField(),
  reason: option(PURCHASE_REASONS)
    .nullish()
    .transform((value) => value ?? null),
  currency: z.string(typed('INVALID_CURRENCY')).regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY'),
  remarks: optionalText(1000),
  /** Bağlantılar: PMS iş emri ve eksiklik raporu numarası. */
  workOrderNo: optionalText(60),
  defectReportNo: optionalText(60),
  /**
   * Kimlikle bağ (ADR 0096 §4): talebi açan arıza ve HSEQ raporu. İçerik hash'ine girmez (liman
   * kodu gibi bağdır; numara metni `defectReportNo` içeriktedir); eski talep bağsızdır.
   */
  defectId: z
    .uuid(typed('INVALID_ID'))
    .nullish()
    .transform((value) => value ?? null),
  hseReportId: z
    .uuid(typed('INVALID_ID'))
    .nullish()
    .transform((value) => value ?? null),
  lines: z.array(requisitionLineSchema).max(MAX_REQUISITION_LINES, 'TOO_MANY_LINES'),
});
export type Requisition = z.output<typeof requisitionSchema>;
export type RequisitionHeader = Omit<Requisition, 'lines'>;

export function parseRequisition(input: unknown): ParseResult<Requisition> {
  const result = requisitionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Gönderilebilir mi: en az bir satır; istenen tarih gemi bugününden önce değil (gelecek tarih
 * gemi sunucusu saatine göre; CLAUDE.md "Zaman").
 */
export function assertSubmittable(
  requisition: Pick<Requisition, 'requiredDate'> & { lines: readonly unknown[] },
  today: string,
): void {
  if (requisition.lines.length === 0) throw new DomainError('NO_LINES');
  if (requisition.requiredDate < today) {
    throw new DomainError('REQUIRED_DATE_PAST', requisition.requiredDate);
  }
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/**
 * Toplam tahmini tutar (ADR 0026 §3): satır miktarı × tahmini birim fiyat. Fiyatı girilmemiş
 * satır varsa tutar bilinmez (`value: null`; sorular #71); `priced` fiyatlı satırların toplamı.
 */
export function requisitionTotal(
  lines: readonly { quantity: number; unitPrice: number | null }[],
): { value: number | null; priced: number } {
  let priced = 0;
  let unknown = false;
  for (const line of lines) {
    if (line.unitPrice === null) unknown = true;
    else priced += line.quantity * line.unitPrice;
  }
  const total = round(priced);
  return { value: unknown ? null : total, priced: total };
}

/** Kural seçiminin tutarı (ADR 0026 §3): temel para biriminde değilse en yüksek kademe. */
export function requisitionAmount(
  requisition: {
    currency: string;
    lines: readonly { quantity: number; unitPrice: number | null }[];
  },
  baseCurrency: string,
): ApprovalAmount {
  return {
    value: requisitionTotal(requisition.lines).value,
    currency: requisition.currency,
    baseCurrency,
  };
}

/**
 * Sıradaki talep numarası (sorular #72): gemi başına yıl içinde sıralı `MR-YYYY-NNNN`. Gemi
 * internetsiz numara verir; tekillik gemi ve numara çiftindedir.
 */
export function nextRequisitionNo(existing: readonly string[], year: number): string {
  let last = 0;
  for (const no of existing) {
    const match = /^MR-(\d{4})-(\d+)$/.exec(no);
    if (match && Number(match[1]) === year) last = Math.max(last, Number(match[2]));
  }
  return `MR-${year}-${String(last + 1).padStart(4, '0')}`;
}

export interface RequisitionContentLine extends RequisitionLine {
  lineNo: number;
}

export interface RequisitionContentInput {
  vesselId: string;
  requisitionNo: string;
  requestDate: string;
  header: RequisitionHeader;
  lines: readonly RequisitionContentLine[];
}

/**
 * Onaylanan içerik (mimari belge §7.3): hash'in girdisi. Satırlar satır numarasıyla sıralanır;
 * kalem adı ve birimi satırdadır, katalog değişse de içerik değişmez. Gemi ve bulut aynı
 * fonksiyonla hesaplar.
 */
export function requisitionContent(input: RequisitionContentInput) {
  const { header } = input;
  return {
    vesselId: input.vesselId,
    requisitionNo: input.requisitionNo,
    requestDate: input.requestDate,
    department: header.department,
    requestedBy: header.requestedBy,
    requiredDate: header.requiredDate,
    port: header.port,
    reason: header.reason,
    currency: header.currency,
    remarks: header.remarks,
    workOrderNo: header.workOrderNo,
    defectReportNo: header.defectReportNo,
    lines: [...input.lines]
      .sort((a, b) => a.lineNo - b.lineNo)
      .map((line) => ({
        lineNo: line.lineNo,
        materialId: line.materialId,
        name: line.name,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        unitPrice: line.unitPrice,
        priority: line.priority,
      })),
  };
}
export type RequisitionContent = ReturnType<typeof requisitionContent>;

/**
 * Talep satırının stok mevcudu (ADR 0111 §A; sahip 2026-10-23: "stokta mevcut ise talep listesinde
 * örneğin 5 adet mevcut desin"): katalog kaleminde geminin defter toplamı (kumanya dahil; bakiye
 * defterden türer, ADR 0026 §2); stok kalemi yoksa 0. Serbest metin kalemde bilinmez (`null`).
 */
export function lineStockOnHand(
  materialId: string | null,
  totals: ReadonlyMap<string, number>,
): number | null {
  if (materialId === null) return null;
  return Math.round((totals.get(materialId) ?? 0) * PRECISION) / PRECISION;
}
